import { useState } from "react";
import { Check, Upload, FileText, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { api } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

export const slug = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

export const SectionHeader = ({ icon: Icon, title, subtitle, step, total }) => (
  <div className="mb-6">
    <div className="mb-3 flex items-center gap-3">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon className="h-5 w-5" />
      </span>
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Étape {step} / {total}
      </span>
    </div>
    <h2 className="font-heading text-2xl font-bold text-foreground sm:text-3xl">{title}</h2>
    {subtitle && <p className="mt-1.5 text-sm text-muted-foreground">{subtitle}</p>}
  </div>
);

export const Field = ({ label, required, hint, children }) => (
  <div className="space-y-2">
    {label && (
      <label className="block text-sm font-semibold text-foreground">
        {label}
        {required && <span className="text-primary"> *</span>}
      </label>
    )}
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export const TextInput = ({ value, onChange, testId, type = "text", placeholder, inputMode }) => (
  <input
    type={type}
    inputMode={inputMode}
    value={value || ""}
    placeholder={placeholder}
    data-testid={testId}
    onChange={(e) => onChange(e.target.value)}
    className="h-12 w-full rounded-xl border border-input bg-white px-4 text-[15px] text-foreground outline-none transition-all placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/15"
  />
);

export const TextArea = ({ value, onChange, testId, placeholder, rows = 4 }) => (
  <textarea
    rows={rows}
    value={value || ""}
    placeholder={placeholder}
    data-testid={testId}
    onChange={(e) => onChange(e.target.value)}
    className="w-full rounded-xl border border-input bg-white p-4 text-[15px] text-foreground outline-none transition-all placeholder:text-slate-400 focus:border-primary focus:ring-2 focus:ring-primary/15"
  />
);

export const OptionCard = ({ label, selected, onClick, multi, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={cn(
      "group flex items-center gap-3 rounded-xl border-2 p-4 text-left transition-all duration-200 active:scale-[0.98]",
      selected
        ? "border-primary bg-primary/[0.06] ring-2 ring-primary/10"
        : "border-border bg-white hover:border-primary/40 hover:bg-slate-50"
    )}
  >
    <span
      className={cn(
        "flex h-5 w-5 shrink-0 items-center justify-center border-2 transition-colors",
        multi ? "rounded-md" : "rounded-full",
        selected ? "border-primary bg-primary text-white" : "border-slate-300 text-transparent"
      )}
    >
      <Check className="h-3.5 w-3.5" strokeWidth={3} />
    </span>
    <span className={cn("text-[15px] font-medium", selected ? "text-foreground" : "text-slate-700")}>
      {label}
    </span>
  </button>
);

export const OptionGrid = ({ options, value, onChange, multi = false, columns = 1, testIdPrefix }) => {
  const isSelected = (opt) => (multi ? (value || []).includes(opt) : value === opt);
  const handle = (opt) => {
    if (multi) {
      const arr = value || [];
      onChange(arr.includes(opt) ? arr.filter((o) => o !== opt) : [...arr, opt]);
    } else {
      onChange(value === opt ? "" : opt);
    }
  };
  return (
    <div className={cn("grid gap-3", columns === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1")}>
      {options.map((opt) => (
        <OptionCard
          key={opt}
          label={opt}
          multi={multi}
          selected={isSelected(opt)}
          onClick={() => handle(opt)}
          testId={`${testIdPrefix}-${slug(opt)}`}
        />
      ))}
    </div>
  );
};

export const Pills = ({ options, value, onChange, testIdPrefix }) => (
  <div className="flex flex-wrap gap-2.5">
    {options.map((opt) => {
      const selected = value === opt;
      return (
        <button
          key={opt}
          type="button"
          data-testid={`${testIdPrefix}-${slug(opt)}`}
          onClick={() => onChange(selected ? "" : opt)}
          className={cn(
            "flex h-12 w-12 items-center justify-center rounded-xl border-2 text-base font-bold transition-all active:scale-95",
            selected
              ? "border-primary bg-primary text-white"
              : "border-border bg-white text-slate-700 hover:border-primary/40"
          )}
        >
          {opt}
        </button>
      );
    })}
  </div>
);

export const PriceUpload = ({ value, onChange }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ path: res.data.path, filename: res.data.filename });
      toast.success("Fichier ajouté");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi du fichier");
    } finally {
      setUploading(false);
    }
  };

  if (value?.path) {
    return (
      <div
        className="flex items-center gap-3 rounded-xl border-2 border-primary/30 bg-primary/[0.04] p-4"
        data-testid="price-file-preview"
      >
        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-foreground">{value.filename}</p>
          <p className="text-xs text-emerald-600">Ajouté avec succès</p>
        </div>
        <button
          type="button"
          data-testid="remove-price-file-button"
          onClick={() => onChange(null)}
          className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <label
      data-testid="price-upload-zone"
      className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center transition-colors hover:border-primary hover:bg-primary/[0.03]"
    >
      <input
        type="file"
        accept="image/*,application/pdf"
        className="hidden"
        data-testid="price-upload-input"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      {uploading ? (
        <Loader2 className="h-7 w-7 animate-spin text-primary" />
      ) : (
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <Upload className="h-5 w-5" />
        </span>
      )}
      <span className="text-sm font-semibold text-foreground">
        {uploading ? "Envoi en cours…" : "Ajouter une photo / capture"}
      </span>
      <span className="text-xs text-muted-foreground">Image ou PDF · max 15 Mo</span>
    </label>
  );
};
